import type { Gender } from '@/modules/Core/types/model/gender'

export enum EmployeeStatus {
  Applicant = 0,
  Active = 1,
  Suspended = 2,
  Terminated = 3,
}

export const EmployeeStatusLabels: Record<EmployeeStatus, string> = {
  [EmployeeStatus.Applicant]: 'متقاضی',
  [EmployeeStatus.Active]: 'فعال',
  [EmployeeStatus.Suspended]: 'معلق',
  [EmployeeStatus.Terminated]: 'قطع همکاری',
}

export enum ApplicantRequestStatus {
  Pending = 0,
  InvitedToInterview = 1,
  Accepted = 2,
  RejectedBeforeInterview = 3,
  RejectedAfterInterview = 4,
}

export const ApplicantRequestStatusLabels: Record<
  ApplicantRequestStatus,
  string
> = {
  [ApplicantRequestStatus.Pending]: 'در حال بررسی',
  [ApplicantRequestStatus.InvitedToInterview]: 'دعوت به مصاحبه',
  [ApplicantRequestStatus.Accepted]: 'تایید شده',
  [ApplicantRequestStatus.RejectedBeforeInterview]: 'رد قبل از مصاحبه',
  [ApplicantRequestStatus.RejectedAfterInterview]: 'رد بعد از مصاحبه',
}

export enum EducationDegree {
  Diploma = 0,
  Associate = 1,
  Bachelor = 2,
  Master = 3,
  Doctorate = 4,
}

export const EducationDegreeLabels: Record<EducationDegree, string> = {
  [EducationDegree.Diploma]: 'دیپلم',
  [EducationDegree.Associate]: 'کاردانی',
  [EducationDegree.Bachelor]: 'کارشناسی',
  [EducationDegree.Master]: 'کارشناسی ارشد',
  [EducationDegree.Doctorate]: 'دکتری',
}

export enum MaritalStatus {
  Single = 0,
  Married = 1,
}

export const MaritalStatusLabels: Record<MaritalStatus, string> = {
  [MaritalStatus.Single]: 'مجرد',
  [MaritalStatus.Married]: 'متأهل',
}

export enum MilitaryServiceStatus {
  Exempt = 0,
  Required = 1,
  Completed = 2,
}

export const MilitaryServiceStatusLabels: Record<
  MilitaryServiceStatus,
  string
> = {
  [MilitaryServiceStatus.Exempt]: 'معاف',
  [MilitaryServiceStatus.Required]: 'مشمول',
  [MilitaryServiceStatus.Completed]: 'انجام شده',
}

export type DateOnlyString = string

export interface CreateEmployeePayload {
  status: EmployeeStatus
  firstName: string
  lastName: string
  positionIds: number[]
  gender: Gender
  phoneNumber: string
  cityId: number
  address: string
  probationStartDate?: DateOnlyString | null
  probationEndDate?: DateOnlyString | null
  contractStartDate?: DateOnlyString | null
  contractEndDate?: DateOnlyString | null
}

export interface UpdateEmployeePayload {
  firstName: string
  lastName: string
  gender: Gender
  phoneNumber: string
  positionIds: number[]
  address: string
  cityId?: number
  probationStartDate?: DateOnlyString | null
  probationEndDate?: DateOnlyString | null
  contractStartDate?: DateOnlyString | null
  contractEndDate?: DateOnlyString | null
}

export interface UpdateEmployeePersonalInfoPayload {
  firstName: string
  lastName: string
  gender: Gender
  positionIds: number[]
  nationalCode: string
  address: string
  cityId: number
  birthDate?: DateOnlyString | null
  landlineNumber?: string | null
}

export interface UpdateEmployeeIdentityInfoPayload {
  birthCertificateNumber: string
  birthCertificateSerial: string
  birthCertificatePageNumber: string
  fatherName: string
  birthPlace: string
  birthCertificateIssueDate: DateOnlyString
  birthCertificateIssuePlace: string
}

export interface UpdateEmployeeFamilyInfoPayload {
  maritalStatus: MaritalStatus
  daughtersCount: number
  sonsCount: number
  marriageDate?: DateOnlyString | null
  spouseName?: string | null
  spouseBirthCertificateNumber?: string | null
  spouseNationalCode?: string | null
}

export interface UpdateEmployeeMilitaryServiceInfoPayload {
  hasMilitaryServiceCard: boolean
  militaryServiceCardIssueDate?: DateOnlyString | null
  militaryServiceStatus: MilitaryServiceStatus
}

export interface UpdateEmployeeEducationalInfoPayload {
  educationDegree: EducationDegree
  fieldOfStudy: string
}

export interface WorkExperienceInfoItemPayload {
  companyName: string
  companyNumber: string
  startDate: DateOnlyString
  endDate?: DateOnlyString | null
}

export interface UpdateEmployeeWorkExperienceInfoPayload {
  workExperiences: WorkExperienceInfoItemPayload[]
}

/** POST /Employees may return bare id or empty body. */
export type CreateEmployeeResponse = number | void

export interface EmployeePositionDto {
  id: number
  title: string
}

export interface EmployeeIdentityInfoDto {
  birthCertificateNumber: string
  birthCertificateSerial: string
  birthCertificatePageNumber: string
  fatherName: string
  birthPlace: string
  birthCertificateIssueDate: DateOnlyString
  birthCertificateIssuePlace: string
}

export interface EmployeeEducationalInfoDto {
  educationDegree: EducationDegree
  fieldOfStudy: string
}

export interface EmployeeMilitaryServiceInfoDto {
  hasMilitaryServiceCard: boolean
  militaryServiceCardIssueDate: DateOnlyString | null
  militaryServiceStatus: MilitaryServiceStatus
}

export interface EmployeeFamilyInfoDto {
  maritalStatus: MaritalStatus
  daughtersCount: number
  sonsCount: number
  marriageDate: DateOnlyString | null
  spouseName: string | null
  spouseBirthCertificateNumber: string | null
  spouseNationalCode: string | null
}

export interface EmployeeWorkExperienceInfoDto {
  companyName: string
  companyNumber: string
  startDate: DateOnlyString
  endDate: DateOnlyString | null
}

/** GET employee profile — nested city from Shop.Contracts.Cities.CityInfo */
export interface CityInfoDto {
  cityId: number
  cityName: string
  provinceId: number
  provinceName: string
}

export function getEmployeeCityId(
  profile: Pick<EmployeeProfileDto, 'cityInfo'>
): number | null {
  return profile.cityInfo?.cityId ?? null
}

export function getEmployeeProvinceId(
  profile: Pick<EmployeeProfileDto, 'cityInfo'>
): number | null {
  return profile.cityInfo?.provinceId ?? null
}

export interface EmployeeProfileDto {
  id: number
  personnelCode: string | null
  firstName: string
  lastName: string
  nationalCode: string | null
  landlineNumber?: string | null
  phoneNumber: string
  email: string | null
  gender: Gender | null
  birthDate: DateOnlyString | null
  address: string | null
  cityInfo: CityInfoDto | null
  probationStartDate: DateOnlyString | null
  probationEndDate: DateOnlyString | null
  contractStartDate: DateOnlyString | null
  contractEndDate: DateOnlyString | null
  status: EmployeeStatus
  applicantRequestStatus: ApplicantRequestStatus | null
  rejectionReason: string | null
  positions: EmployeePositionDto[]
  identityInfo: EmployeeIdentityInfoDto | null
  educationalInfo: EmployeeEducationalInfoDto | null
  militaryServiceInfo: EmployeeMilitaryServiceInfoDto | null
  familyInfo: EmployeeFamilyInfoDto | null
  workExperienceInfos: EmployeeWorkExperienceInfoDto[]
}

export interface EmployeeListItemDto {
  id: number
  firstName: string
  lastName: string
  phoneNumber: string
  educationDegree: EducationDegree | null
  positionName: string
}

export interface UnevaluatedEmployeeItem {
  id: number
  firstName: string | null
  lastName: string | null
}

export type PaginatedEmployeesDto = Paginated<EmployeeListItemDto[]>

export interface ImportEmployeesFailedRowDto {
  sheetName: string
  rowNumber?: number
  errors: string[]
}

export interface ImportEmployeesResponseDto {
  totalRowsCount?: number
  createdCount?: number
  workExperiencesCount?: number
  failedRows: ImportEmployeesFailedRowDto[]
}

export interface ApplicantListItemDto {
  id: number
  firstName: string
  lastName: string
  phoneNumber: string
  educationDegree: EducationDegree | null
  positionName: string
  applicantRequestStatus: ApplicantRequestStatus
}

export type PaginatedApplicantsDto = Paginated<ApplicantListItemDto[]>

export interface ChangeEmployeeStatusPayload {
  status: EmployeeStatus
}

export interface ChangeApplicantRequestStatusPayload {
  applicantRequestStatus: ApplicantRequestStatus
  rejectionReason?: string | null
  personnelCode?: string | null
  positionIds?: number[] | null
  probationStartDate?: DateOnlyString | null
  probationEndDate?: DateOnlyString | null
  contractStartDate?: DateOnlyString | null
  contractEndDate?: DateOnlyString | null
}

export type {
  HrPositionChildItem,
  HrPositionDetail,
  HrPositionDetailEmployee,
  HrPositionListEmployee,
  HrPositionListItem,
} from '@/modules/OrgChart/types/api/position'

export interface ProvinceListItem {
  id: number
  name: string
}

export interface ProvinceCityListItem {
  id: number
  name: string
}
